import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { MouseEvent, ReactNode, useCallback, useEffect, useRef } from "react";

import Button from "@/components/atoms/forms/Button";
import { usePortal } from "@/hooks";

import styles from "./Modal.module.css";

interface ModalProps {
  open: boolean;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  portalEl?: string;
  onClose: () => void;
}

export default function Modal({
  open,
  children,
  portalEl,
  onClose,
}: ModalProps) {
  const Portal = usePortal(portalEl);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;

    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  const handleNativeClose = useCallback(() => onClose(), [onClose]);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLDialogElement>) => {
      const rect = dialogRef.current?.getBoundingClientRect();
      if (!rect) return;
      if (
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom
      ) {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <Portal>
      <dialog
        ref={dialogRef}
        className={styles.dialog}
        onClick={handleClick}
        onClose={handleNativeClose}
        aria-labelledby="modal-title"
      >
        <Button
          aria-label="Close modal"
          icon={faXmark}
          className={styles.closeButton}
          rounded
          onClick={onClose}
        />

        <div className={styles.body}>{children}</div>
      </dialog>
    </Portal>
  );
}
